import { useState, useEffect, useRef } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { commissions, agents as agentsApi, lenders as lendersApi, dataImport } from '@/lib/api';
import { formatCurrency, formatPercent, formatPercentClean, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Search, Trash2, Edit2, DollarSign, Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle, Home, Briefcase, CheckSquare, XSquare, ArrowRight, ChevronDown, ChevronRight, Calendar, FileText } from 'lucide-react';

export default function Commissions() {
  const { user } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const isAgent = user?.role === 'agent';
  const [data, setData] = useState([]);
  const [agents, setAgents] = useState([]);
  const [lendersList, setLendersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  
  // Import mapping state
  const [importStep, setImportStep] = useState('upload'); // 'upload', 'mapping', 'result'
  const [previewData, setPreviewData] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [systemFields, setSystemFields] = useState([]);
  
  // Batch PDF import state
  const [showBatchPdfModal, setShowBatchPdfModal] = useState(false);
  const [batchPdfFiles, setBatchPdfFiles] = useState([]);
  const [batchImportLoading, setBatchImportLoading] = useState(false);
  const [batchImportResult, setBatchImportResult] = useState(null);
  const batchFileInputRef = useRef(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Monthly collapsible state
  const [expandedMonths, setExpandedMonths] = useState({});
  
  // Business unit labels
  const buLabel = businessUnit === 'insurance' ? 'Insurance & Investment' : 'Mortgage';
  const BuIcon = businessUnit === 'insurance' ? Briefcase : Home;
  
  const [formData, setFormData] = useState({
    dlc_statement_no: '',
    statement_date: new Date().toISOString().split('T')[0],
    agent_id: '',
    co_agent_id: '',
    lender: '',
    product: 'Mortgage',
    velocity_ref: '',
    description: '',
    mortgage_loan: '',
    ff_percentage: '0',
    vb_percentage: '0',
    extra_bonus_percentage: '0',
    agent_split_percentage: '85',  // Now stored as percentage (85 = 85%)
    co_agent_split_percentage: '0',
    co_brokering_comm_percentage: '0',
    memo: '',
    payment_date: ''
  });

  useEffect(() => {
    loadData();
  }, [businessUnit]); // Reload when business unit changes

  const loadData = async () => {
    setLoading(true);
    try {
      const [commRes, agentsRes, lendersRes] = await Promise.all([
        commissions.list(),
        agentsApi.list(),
        lendersApi.list()
      ]);
      setData(commRes.data);
      setAgents(agentsRes.data);
      setLendersList(lendersRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      dlc_statement_no: '',
      statement_date: new Date().toISOString().split('T')[0],
      agent_id: '',
      co_agent_id: '',
      lender: '',
      product: 'Mortgage',
      velocity_ref: '',
      description: '',
      mortgage_loan: '',
      ff_percentage: '0',
      vb_percentage: '0',
      extra_bonus_percentage: '0',
      agent_split_percentage: '85',  // Now stored as percentage (85 = 85%)
      co_agent_split_percentage: '0',
      co_brokering_comm_percentage: '0',
      memo: '',
      payment_date: ''
    });
    setEditingId(null);
  };

  const openEdit = (commission) => {
    setFormData({
      dlc_statement_no: commission.dlc_statement_no,
      statement_date: commission.statement_date,
      agent_id: commission.agent_id,
      co_agent_id: commission.co_agent_id || '',
      lender: commission.lender,
      product: commission.product,
      velocity_ref: commission.velocity_ref || '',
      description: commission.description,
      mortgage_loan: commission.mortgage_loan.toString(),
      // Convert decimal to basis points (multiply by 10000)
      ff_percentage: (commission.ff_percentage * 10000).toString(),
      vb_percentage: (commission.vb_percentage * 10000).toString(),
      extra_bonus_percentage: (commission.extra_bonus_percentage * 10000).toString(),
      // Convert decimal to percentage (0.85 -> 85)
      agent_split_percentage: (commission.agent_split_percentage * 100).toString(),
      co_agent_split_percentage: (commission.co_agent_split_percentage * 100).toString(),
      co_brokering_comm_percentage: ((commission.co_brokering_comm_percentage || 0) * 100).toString(),
      memo: commission.memo || '',
      payment_date: commission.payment_date || ''
    });
    setEditingId(commission.id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        mortgage_loan: parseFloat(formData.mortgage_loan) || 0,
        // Convert basis points to decimal (divide by 10000)
        ff_percentage: (parseFloat(formData.ff_percentage) || 0) / 10000,
        vb_percentage: (parseFloat(formData.vb_percentage) || 0) / 10000,
        extra_bonus_percentage: (parseFloat(formData.extra_bonus_percentage) || 0) / 10000,
        // Convert percentage to decimal (85 -> 0.85)
        agent_split_percentage: (parseFloat(formData.agent_split_percentage) || 85) / 100,
        co_agent_split_percentage: (parseFloat(formData.co_agent_split_percentage) || 0) / 100,
        co_brokering_comm_percentage: (parseFloat(formData.co_brokering_comm_percentage) || 0) / 100,
        memo: formData.memo || '',
        co_agent_id: formData.co_agent_id || null,
        payment_date: formData.payment_date || null
      };

      if (editingId) {
        await commissions.update(editingId, payload);
        toast.success('Commission updated');
      } else {
        await commissions.create(payload);
        toast.success('Commission created');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this commission?')) return;
    try {
      await commissions.delete(id);
      toast.success('Commission deleted');
      loadData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Accept both Excel and PDF files
      if (!file.name.match(/\.(xlsx|xls|xlsm|pdf)$/i)) {
        toast.error('Please select an Excel file (.xlsx, .xls, .xlsm) or PDF file');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
      setImportStep('upload');
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setImportLoading(true);
    
    try {
      let response;
      // Check if it's a PDF file
      if (selectedFile.name.toLowerCase().endsWith('.pdf')) {
        response = await dataImport.previewPdf(selectedFile);
        // Store PDF info for display
        if (response.data.statement_no) {
          toast.success(`PDF loaded! Statement #${response.data.statement_no} - ${response.data.total_rows} transactions found.`);
        } else {
          toast.success(`PDF loaded! ${response.data.total_rows} rows found.`);
        }
      } else {
        response = await dataImport.preview(selectedFile);
        toast.success(`File loaded! ${response.data.total_rows} rows found.`);
      }
      setPreviewData(response.data);
      setSystemFields(response.data.system_fields || []);
      // Set auto-detected mappings
      setColumnMappings(response.data.auto_mappings || {});
      setImportStep('mapping');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to read file. Please check the format.';
      toast.error(errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData || !previewData.preview_data) {
      toast.error('No data to import');
      return;
    }
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      let response;
      // Check if this was a PDF import (by checking file_type in previewData)
      if (previewData.file_type === 'pdf') {
        response = await dataImport.executePdf(previewData.session_id, columnMappings);
      } else {
        // Use session_id to execute import on full file
        response = await dataImport.execute(previewData.session_id, columnMappings);
      }
      setImportResult({
        success: true,
        message: response.data.message,
        imported: response.data.imported,
        skipped: response.data.skipped,
        errors: response.data.errors,
        created_agents: response.data.created_agents,
        statement_no: response.data.statement_no
      });
      setImportStep('result');
      toast.success(`Import completed! ${response.data.imported} commissions imported.`);
      loadData(); // Refresh the table
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Import failed. Please check your mappings.';
      setImportResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleMappingChange = (systemField, fileColumn) => {
    setColumnMappings(prev => ({
      ...prev,
      [systemField]: fileColumn
    }));
  };

  const resetImportModal = () => {
    setSelectedFile(null);
    setImportResult(null);
    setImportStep('upload');
    setPreviewData(null);
    setColumnMappings({});
    setShowImportModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Batch PDF Import handlers
  const handleBatchPdfSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length !== files.length) {
      toast.warning(`${files.length - pdfFiles.length} non-PDF files were excluded`);
    }
    setBatchPdfFiles(pdfFiles);
    setBatchImportResult(null);
  };

  const handleBatchPdfImport = async () => {
    if (batchPdfFiles.length === 0) {
      toast.error('Please select PDF files');
      return;
    }
    
    setBatchImportLoading(true);
    setBatchImportResult(null);
    
    try {
      const response = await dataImport.batchImportPdfs(batchPdfFiles);
      setBatchImportResult(response.data);
      toast.success(`Imported ${response.data.total_imported} records from ${response.data.files_processed} files`);
      loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Batch import failed';
      toast.error(errorMsg);
      setBatchImportResult({ error: errorMsg });
    } finally {
      setBatchImportLoading(false);
    }
  };

  const resetBatchPdfModal = () => {
    setBatchPdfFiles([]);
    setBatchImportResult(null);
    setShowBatchPdfModal(false);
    if (batchFileInputRef.current) {
      batchFileInputRef.current.value = '';
    }
  };

  const filteredData = data.filter(c =>
    c.agent_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.description?.toLowerCase().includes(search.toLowerCase()) ||
    c.lender?.toLowerCase().includes(search.toLowerCase())
  );

  // Group commissions by DLC Statement # and sort chronologically
  const groupedByStatement = filteredData
    .reduce((groups, commission) => {
      const stmtNo = commission.dlc_statement_no || 'No Statement #';
      if (!groups[stmtNo]) {
        groups[stmtNo] = {
          key: stmtNo,
          label: stmtNo,
          commissions: [],
          totalGross: 0,
          totalNet: 0,
          totalAgent: 0,
          totalBrokerage: 0,
          count: 0
        };
      }
      groups[stmtNo].commissions.push(commission);
      groups[stmtNo].totalGross += commission.gross_commission || 0;
      groups[stmtNo].totalNet += commission.net_commission || 0;
      groups[stmtNo].totalAgent += commission.agent_commission || 0;
      groups[stmtNo].totalBrokerage += commission.brokerage_commission || 0;
      groups[stmtNo].count += 1;
      return groups;
    }, {});

  // Parse DLC Statement # for proper chronological sorting (e.g., "2025-1" -> {year: 2025, num: 1})
  const parseStatementNo = (stmtNo) => {
    const match = stmtNo.match(/^(\d{4})-(\d+)$/);
    if (match) {
      return { year: parseInt(match[1]), num: parseInt(match[2]) };
    }
    return { year: 0, num: 0 };
  };

  // Convert to sorted array - reverse chronological order (newest to oldest: 2026-200, 2026-199, ... 2025-2, 2025-1)
  const statementData = Object.values(groupedByStatement).sort((a, b) => {
    const parsedA = parseStatementNo(a.key);
    const parsedB = parseStatementNo(b.key);
    
    // First compare by year (descending - newest first)
    if (parsedA.year !== parsedB.year) {
      return parsedB.year - parsedA.year;
    }
    // Then by statement number (descending - highest first)
    return parsedB.num - parsedA.num;
  });

  // Toggle statement expansion
  const toggleStatement = (stmtKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [stmtKey]: !prev[stmtKey]
    }));
  };

  // Expand/Collapse all
  const expandAll = () => {
    const allExpanded = {};
    statementData.forEach(s => allExpanded[s.key] = true);
    setExpandedMonths(allExpanded);
  };

  const collapseAll = () => {
    setExpandedMonths({});
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map(c => c.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const deletePromises = Array.from(selectedIds).map(id => 
        commissions.delete(id)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} commission(s) deleted successfully`);
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to delete some commissions');
    }
  };

  const handleBulkExport = () => {
    const selectedData = filteredData.filter(c => selectedIds.has(c.id));
    if (selectedData.length === 0) {
      toast.error('No records selected for export');
      return;
    }
    
    const exportData = selectedData.map(c => ({
      'Statement No': c.dlc_statement_no || '',
      'Statement Date': c.statement_date,
      'Agent': c.agent_name,
      'Co-Agent': c.co_agent_name || '',
      'Lender': c.lender || '',
      'Description': c.description || '',
      'Mortgage Loan': c.mortgage_loan || '',
      'Gross Commission': c.gross_commission,
      'Agent Commission': c.agent_commission,
      'Brokerage Commission': c.brokerage_commission,
      'Royalty + GST': c.royalty_plus_gst || 0
    }));
    
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commissions_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${selectedData.length} record(s) exported`);
  };

  const totalGross = filteredData.reduce((sum, c) => sum + c.gross_commission, 0);
  
  // For agents, calculate their total net commission (what they received)
  const totalAgentNetCommission = isAgent 
    ? filteredData.reduce((sum, c) => {
        // If agent is primary agent, use agent_commission, else use co_agent_commission
        const myCommission = c.agent_id === user?.agent_id ? c.agent_commission : c.co_agent_commission;
        return sum + (myCommission || 0);
      }, 0)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="commissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {isAgent ? 'My Commissions' : 'Commissions'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAgent ? 'View your commission records' : 'Manage mortgage commission records'}
          </p>
        </div>
        {!isAgent && (
          <div className="flex gap-2">
            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={handleBulkExport}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <Download className="w-4 h-4 mr-2" /> Export ({selectedIds.size})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedIds.size})
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-slate-500"
                >
                  <XSquare className="w-4 h-4 mr-2" /> Clear
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              data-testid="import-excel-btn"
              className="border-slate-300"
            >
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBatchPdfModal(true)}
              data-testid="batch-pdf-import-btn"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <FileText className="w-4 h-4 mr-2" /> Batch PDF
            </Button>
          <Button
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-commission-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Commission
          </Button>
        </div>
        )}
      </div>

      {/* Stats & Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Card className="flex-1 border-slate-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">
                {isAgent ? 'My Total Commission' : 'Total Gross'}
              </p>
              <p className="text-xl font-bold font-mono text-slate-900">
                {formatCurrency(isAgent ? totalAgentNetCommission : totalGross)}
              </p>
            </div>
          </CardContent>
        </Card>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search commissions..."
            data-testid="search-input"
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Expand/Collapse Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll} className="text-xs">
            <ChevronDown className="w-3 h-3 mr-1" /> Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} className="text-xs">
            <ChevronRight className="w-3 h-3 mr-1" /> Collapse All
          </Button>
          <span className="text-sm text-slate-500 ml-2">
            {statementData.length} statements, {filteredData.length} transactions
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-slate-600">
            Total Gross: <span className="font-bold text-emerald-600">{formatCurrency(filteredData.reduce((sum, c) => sum + (c.gross_commission || 0), 0))}</span>
          </div>
          <div className="text-slate-600">
            Total Agent: <span className="font-bold text-blue-600">{formatCurrency(filteredData.reduce((sum, c) => sum + (c.agent_commission || 0), 0))}</span>
          </div>
        </div>
      </div>

      {/* Collapsible Statement Sections */}
      <div className="space-y-3">
        {statementData.length === 0 ? (
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="py-12 text-center text-slate-500">
              No commissions found
            </CardContent>
          </Card>
        ) : (
          statementData.map(stmt => (
            <Card key={stmt.key} className="border-slate-100 shadow-sm overflow-hidden">
              {/* Statement Header - Clickable */}
              <div 
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white cursor-pointer hover:bg-slate-100 transition-colors flex items-center justify-between"
                onClick={() => toggleStatement(stmt.key)}
                data-testid={`stmt-header-${stmt.key}`}
              >
                <div className="flex items-center gap-3">
                  {expandedMonths[stmt.key] ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                    DLC Statement # {stmt.label}
                  </span>
                  <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                    {stmt.count} transaction{stmt.count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-slate-600">
                    Gross: <span className="font-bold text-emerald-600">{formatCurrency(stmt.totalGross)}</span>
                  </div>
                  <div className="text-slate-600">
                    Net: <span className="font-bold text-emerald-700">{formatCurrency(stmt.totalNet)}</span>
                  </div>
                  <div className="text-slate-600">
                    Agent: <span className="font-bold text-blue-600">{formatCurrency(stmt.totalAgent)}</span>
                  </div>
                  {!isAgent && (
                    <div className="text-slate-600">
                      Brokerage: <span className="font-bold text-amber-600">{formatCurrency(stmt.totalBrokerage)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded Content - Table */}
              {expandedMonths[stmt.key] && (
                <div className="border-t border-slate-200 overflow-x-auto relative">
                  {isAgent ? (
                    /* Agent View Table */
                    <Table className="data-table min-w-[2400px] border-collapse">
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[50px] min-w-[50px] text-center">#</TableHead>
                          <TableHead style={{ position: 'sticky', left: '50px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[150px] min-w-[150px] text-center">Agent</TableHead>
                          <TableHead style={{ position: 'sticky', left: '200px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[100px] min-w-[100px] text-center">Statement Date</TableHead>
                          <TableHead style={{ position: 'sticky', left: '300px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[150px] min-w-[150px] text-center">Co-Agent</TableHead>
                          <TableHead style={{ position: 'sticky', left: '450px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[90px] min-w-[90px] text-center">Lender</TableHead>
                          <TableHead style={{ position: 'sticky', left: '540px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[70px] min-w-[70px] text-center">Product</TableHead>
                          <TableHead style={{ position: 'sticky', left: '610px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[90px] min-w-[90px] text-center">Velocity Ref.</TableHead>
                          <TableHead style={{ position: 'sticky', left: '700px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[180px] min-w-[180px] text-center border-r-2 border-slate-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Description</TableHead>
                          <TableHead className="min-w-[120px] text-center">Mortgage Loan</TableHead>
                          <TableHead className="min-w-[70px] text-center">FF %</TableHead>
                          <TableHead className="min-w-[100px] text-center">FF Comm</TableHead>
                          <TableHead className="min-w-[70px] text-center">VB %</TableHead>
                          <TableHead className="min-w-[100px] text-center">VB Comm</TableHead>
                          <TableHead className="min-w-[90px] text-center">Extra Bonus %</TableHead>
                          <TableHead className="min-w-[120px] text-center">Extra Bonus Comm</TableHead>
                          <TableHead className="min-w-[120px] text-center">Gross Commission</TableHead>
                          <TableHead className="min-w-[100px] text-center">Royalty 5%</TableHead>
                          <TableHead className="min-w-[120px] text-center">5% GST on Royalty</TableHead>
                          <TableHead className="min-w-[110px] text-center">Royalty + GST</TableHead>
                          <TableHead className="min-w-[120px] text-center bg-emerald-100 text-emerald-800 font-bold">Net Commission</TableHead>
                          <TableHead className="min-w-[100px] text-center">Agent Split</TableHead>
                          <TableHead className="min-w-[130px] text-center bg-blue-100 text-blue-800 font-bold">Agent Commission</TableHead>
                          <TableHead className="min-w-[110px] text-center">Co-Agent Split</TableHead>
                          <TableHead className="min-w-[140px] text-center bg-violet-100 text-violet-800 font-bold">Co-Agent Commission</TableHead>
                          <TableHead className="min-w-[110px] text-center">Payment Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stmt.commissions.map((c, index) => (
                          <TableRow key={c.id} className="table-row border-b border-slate-100" data-testid={`commission-row-${c.id}`}>
                            <TableCell style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'white' }} className="w-[50px] min-w-[50px] font-mono text-sm">{index + 1}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '50px', zIndex: 10, backgroundColor: 'white' }} className="w-[150px] min-w-[150px] font-medium text-slate-900">{c.agent_name}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '200px', zIndex: 10, backgroundColor: 'white' }} className="w-[100px] min-w-[100px] font-mono text-sm">{formatDate(c.statement_date)}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '300px', zIndex: 10, backgroundColor: 'white' }} className="w-[150px] min-w-[150px] text-slate-600">{c.co_agent_name || '-'}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '450px', zIndex: 10, backgroundColor: 'white' }} className="w-[90px] min-w-[90px]">{c.lender}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '540px', zIndex: 10, backgroundColor: 'white' }} className="w-[70px] min-w-[70px]">{c.product}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '610px', zIndex: 10, backgroundColor: 'white' }} className="w-[90px] min-w-[90px] text-center">{c.velocity_ref || '-'}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '700px', zIndex: 10, backgroundColor: 'white' }} className="w-[180px] min-w-[180px] max-w-[180px] truncate border-r-2 border-slate-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" title={c.description}>{c.description}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.mortgage_loan)}</TableCell>
                            <TableCell className="text-right font-mono">{formatPercent(c.ff_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.ff_commission)}</TableCell>
                            <TableCell className="text-right font-mono">{formatPercent(c.vb_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.vb_commission)}</TableCell>
                            <TableCell className="text-right font-mono">{formatPercent(c.extra_bonus_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.extra_bonus_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600 font-medium">{formatCurrency(c.gross_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.royalty)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.gst_on_royalty)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.royalty_plus_gst)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-emerald-50 text-emerald-700">{formatCurrency(c.net_commission)}</TableCell>
                            <TableCell className="text-right font-mono">{formatPercentClean(c.agent_split_percentage)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-blue-50 text-blue-700">{formatCurrency(c.agent_commission)}</TableCell>
                            <TableCell className="text-right font-mono">{formatPercentClean(c.co_agent_split_percentage)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-violet-50 text-violet-700">{formatCurrency(c.co_agent_commission)}</TableCell>
                            <TableCell className="text-center font-mono text-sm">{c.payment_date ? formatDate(c.payment_date) : <span className="text-amber-600">Pending</span>}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    /* Admin View Table */
                    <Table className="data-table min-w-[2840px] border-collapse">
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead style={{ position: 'sticky', left: 0, zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[40px] min-w-[40px] text-center">
                            <Checkbox
                              checked={stmt.commissions.every(c => selectedIds.has(c.id)) && stmt.commissions.length > 0}
                              onCheckedChange={() => {
                                const stmtIds = stmt.commissions.map(c => c.id);
                                const allSelected = stmtIds.every(id => selectedIds.has(id));
                                if (allSelected) {
                                  setSelectedIds(prev => {
                                    const newSet = new Set(prev);
                                    stmtIds.forEach(id => newSet.delete(id));
                                    return newSet;
                                  });
                                } else {
                                  setSelectedIds(prev => new Set([...prev, ...stmtIds]));
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead style={{ position: 'sticky', left: '40px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[50px] min-w-[50px] text-center">#</TableHead>
                          <TableHead style={{ position: 'sticky', left: '90px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[140px] min-w-[140px] text-center">Agent</TableHead>
                          <TableHead style={{ position: 'sticky', left: '230px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[100px] min-w-[100px] text-center">Statement Date</TableHead>
                          <TableHead style={{ position: 'sticky', left: '330px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[140px] min-w-[140px] text-center">Co-Agent</TableHead>
                          <TableHead style={{ position: 'sticky', left: '470px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[90px] min-w-[90px] text-center">Lender</TableHead>
                          <TableHead style={{ position: 'sticky', left: '560px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[70px] min-w-[70px] text-center">Product</TableHead>
                          <TableHead style={{ position: 'sticky', left: '630px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[90px] min-w-[90px] text-center">Velocity Ref.</TableHead>
                          <TableHead style={{ position: 'sticky', left: '720px', zIndex: 20, backgroundColor: '#f1f5f9' }} className="w-[180px] min-w-[180px] text-center border-r-2 border-slate-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Description</TableHead>
                          <TableHead className="min-w-[110px] text-center">Mortgage Loan</TableHead>
                          <TableHead className="min-w-[55px] text-center">FF %</TableHead>
                          <TableHead className="min-w-[90px] text-center">FF Comm</TableHead>
                          <TableHead className="min-w-[55px] text-center">VB %</TableHead>
                          <TableHead className="min-w-[90px] text-center">VB Comm</TableHead>
                          <TableHead className="min-w-[60px] text-center">Bonus %</TableHead>
                          <TableHead className="min-w-[100px] text-center">Bonus Comm</TableHead>
                          <TableHead className="min-w-[110px] text-center">Gross Commission</TableHead>
                          <TableHead className="min-w-[90px] text-center">Royalty 5%</TableHead>
                          <TableHead className="min-w-[100px] text-center">5% GST on Royalty</TableHead>
                          <TableHead className="min-w-[100px] text-center">Royalty + GST</TableHead>
                          <TableHead className="min-w-[110px] text-center bg-emerald-100 text-emerald-800 font-bold">Net Commission</TableHead>
                          <TableHead className="min-w-[70px] text-center bg-violet-100 text-violet-800">Co-Brok %</TableHead>
                          <TableHead className="min-w-[100px] text-center bg-violet-100 text-violet-800">Co-Brok Base</TableHead>
                          <TableHead className="min-w-[70px] text-center">Agt Split</TableHead>
                          <TableHead className="min-w-[110px] text-center bg-blue-100 text-blue-800 font-bold">Agent Comm</TableHead>
                          <TableHead className="min-w-[70px] text-center">Co-Agt Split</TableHead>
                          <TableHead className="min-w-[110px] text-center bg-violet-100 text-violet-800 font-bold">Co-Agent Comm</TableHead>
                          <TableHead className="min-w-[120px] text-center bg-amber-100 text-amber-800 font-bold">Brokerage Comm</TableHead>
                          <TableHead className="min-w-[100px] text-center">Payment Date</TableHead>
                          <TableHead className="min-w-[150px] text-center">Memo</TableHead>
                          <TableHead className="min-w-[80px] text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stmt.commissions.map((c, index) => (
                          <TableRow 
                            key={c.id} 
                            className={`table-row border-b border-slate-100 ${selectedIds.has(c.id) ? 'bg-blue-50' : ''}`}
                            data-testid={`commission-row-${c.id}`}
                          >
                            <TableCell style={{ position: 'sticky', left: 0, zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[40px] min-w-[40px] text-center">
                              <Checkbox
                                checked={selectedIds.has(c.id)}
                                onCheckedChange={() => toggleSelect(c.id)}
                                data-testid={`select-checkbox-${c.id}`}
                              />
                            </TableCell>
                            <TableCell style={{ position: 'sticky', left: '40px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[50px] min-w-[50px] font-mono text-sm">{index + 1}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '90px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[140px] min-w-[140px] font-medium text-slate-900">{c.agent_name}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '230px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[100px] min-w-[100px] font-mono text-sm">{formatDate(c.statement_date)}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '330px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[140px] min-w-[140px] text-slate-600">{c.co_agent_name || '-'}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '470px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[90px] min-w-[90px]">{c.lender}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '560px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[70px] min-w-[70px]">{c.product}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '630px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[90px] min-w-[90px] text-center">{c.velocity_ref || '-'}</TableCell>
                            <TableCell style={{ position: 'sticky', left: '720px', zIndex: 10, backgroundColor: selectedIds.has(c.id) ? '#eff6ff' : 'white' }} className="w-[180px] min-w-[180px] max-w-[180px] truncate border-r-2 border-slate-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" title={c.description}>{c.description}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.mortgage_loan)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercent(c.ff_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.ff_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercent(c.vb_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.vb_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercent(c.extra_bonus_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.extra_bonus_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600 font-medium">{formatCurrency(c.gross_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.royalty)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.gst_on_royalty)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.royalty_plus_gst)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-emerald-50 text-emerald-700">{formatCurrency(c.net_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercentClean(c.co_brokering_comm_percentage)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(c.co_brokering_base_comm)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercentClean(c.agent_split_percentage)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-blue-50 text-blue-700">{formatCurrency(c.agent_commission)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatPercentClean(c.co_agent_split_percentage)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-violet-50 text-violet-700">{formatCurrency(c.co_agent_commission)}</TableCell>
                            <TableCell className="text-right font-mono font-bold bg-amber-50 text-amber-700">{formatCurrency(c.brokerage_commission)}</TableCell>
                            <TableCell className="text-center font-mono text-sm">{c.payment_date ? formatDate(c.payment_date) : <span className="text-amber-600">Pending</span>}</TableCell>
                            <TableCell className="max-w-[150px] truncate text-sm text-slate-600" title={c.memo}>{c.memo || '-'}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEdit(c)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`edit-btn-${c.id}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(c.id)}
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-btn-${c.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
                    Scroll horizontally to see all columns →
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="commission-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingId ? 'Edit Commission' : 'Add Commission'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>DLC Statement #</Label>
                <Input
                  value={formData.dlc_statement_no}
                  onChange={(e) => setFormData({ ...formData, dlc_statement_no: e.target.value })}
                  placeholder="2026-01"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Statement Date</Label>
                <Input
                  type="date"
                  value={formData.statement_date}
                  onChange={(e) => setFormData({ ...formData, statement_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agent *</Label>
                <Select
                  value={formData.agent_id}
                  onValueChange={(v) => setFormData({ ...formData, agent_id: v })}
                >
                  <SelectTrigger data-testid="agent-select">
                    <SelectValue placeholder="Select agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Co-Agent (Optional)</Label>
                <Select
                  value={formData.co_agent_id || "none"}
                  onValueChange={(v) => setFormData({ ...formData, co_agent_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select co-agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lender</Label>
                <Input
                  value={formData.lender}
                  onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                  placeholder="MCAP, TD Bank, etc."
                  list="lenders-list"
                  required
                />
                <datalist id="lenders-list">
                  {lendersList.map((l, i) => <option key={i} value={l} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Product</Label>
                <Input
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  placeholder="Mortgage"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Velocity Ref.</Label>
              <Input
                value={formData.velocity_ref}
                onChange={(e) => setFormData({ ...formData, velocity_ref: e.target.value })}
                placeholder="Velocity reference number"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="VUTFI-16716-1 VB-NV Client Name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Mortgage Loan Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.mortgage_loan}
                onChange={(e) => setFormData({ ...formData, mortgage_loan: e.target.value })}
                placeholder="250000"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>FF % (basis points)</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.ff_percentage}
                  onChange={(e) => setFormData({ ...formData, ff_percentage: e.target.value })}
                  placeholder="50 = 0.50%"
                />
                <p className="text-xs text-slate-500">100 bp = 1%</p>
              </div>
              <div className="space-y-2">
                <Label>VB % (basis points)</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.vb_percentage}
                  onChange={(e) => setFormData({ ...formData, vb_percentage: e.target.value })}
                  placeholder="40 = 0.40%"
                />
                <p className="text-xs text-slate-500">100 bp = 1%</p>
              </div>
              <div className="space-y-2">
                <Label>Extra Bonus % (basis points)</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.extra_bonus_percentage}
                  onChange={(e) => setFormData({ ...formData, extra_bonus_percentage: e.target.value })}
                  placeholder="30 = 0.30%"
                />
                <p className="text-xs text-slate-500">100 bp = 1%</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agent Split %</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.agent_split_percentage}
                  onChange={(e) => setFormData({ ...formData, agent_split_percentage: e.target.value })}
                  placeholder="e.g. 85 for 85%"
                />
              </div>
              <div className="space-y-2">
                <Label>Co-Agent Split %</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.co_agent_split_percentage}
                  onChange={(e) => setFormData({ ...formData, co_agent_split_percentage: e.target.value })}
                  placeholder="e.g. 15 for 15%"
                />
              </div>
            </div>

            {/* Co-Brokering Fields - Only shown when co-agent is selected */}
            {formData.co_agent_id && (
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                <Label className="text-violet-700 font-semibold mb-2 block">Co-Brokering Settings</Label>
                <div className="space-y-2">
                  <Label>Co-Brokering Comm %</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={formData.co_brokering_comm_percentage}
                    onChange={(e) => setFormData({ ...formData, co_brokering_comm_percentage: e.target.value })}
                    placeholder="e.g. 50 for 50%"
                  />
                  <p className="text-xs text-violet-600">Co-Brokering Base Comm = Gross × Co-Brokering %. Agent/Co-Agent splits will be based on this amount.</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Payment Date <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Memo (Comments)</Label>
              <Input
                type="text"
                value={formData.memo}
                onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                placeholder="Enter any notes or comments"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-commission-btn">
                {editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Excel Modal with Mapping */}
      <Dialog open={showImportModal} onOpenChange={(open) => !open && resetImportModal()}>
        <DialogContent className={`${importStep === 'mapping' ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : 'max-w-lg'}`} data-testid="import-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FileSpreadsheet className="w-5 h-5" />
              Import Commission Data
              {importStep === 'mapping' && <span className="text-sm font-normal text-slate-500 ml-2">- Step 2: Map Columns</span>}
            </DialogTitle>
            <DialogDescription>
              {importStep === 'upload' && 'Upload an Excel file (.xlsx, .xls, .xlsm) to import commission records.'}
              {importStep === 'mapping' && 'Map your Excel columns to the system fields below. Scroll down to see all fields.'}
              {importStep === 'result' && 'Import complete.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Step 1: File Upload */}
            {importStep === 'upload' && (
              <>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                    ${selectedFile ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.xlsm,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle className="w-10 h-10 text-emerald-500" />
                      <p className="font-medium text-slate-900">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                        {selectedFile.name.toLowerCase().endsWith('.pdf') && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">PDF</span>
                        )}
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setSelectedFile(null); 
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-slate-500"
                      >
                        Choose different file
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-slate-400" />
                      <p className="font-medium text-slate-700">Click to upload Excel or PDF file</p>
                      <p className="text-sm text-slate-500">Supports .xlsx, .xls, .xlsm, and .pdf (DLC Commission Statements)</p>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <p className="font-medium text-slate-700 mb-2">Supported Formats:</p>
                  <ul className="space-y-1 text-slate-600 list-disc list-inside">
                    <li><span className="font-medium">Excel files:</span> First row should contain column headers</li>
                    <li><span className="font-medium">PDF files:</span> DLC Commission Statement PDFs will be automatically parsed</li>
                    <li>You will be able to map columns in the next step</li>
                  </ul>
                </div>
              </>
            )}

            {/* Step 2: Column Mapping */}
            {importStep === 'mapping' && previewData && (
              <>
                {/* PDF-specific info banner */}
                {previewData.file_type === 'pdf' && previewData.statement_no && (
                  <div className="bg-violet-50 rounded-lg p-3 text-sm text-violet-800 mb-2">
                    <p className="font-medium">DLC Commission Statement #{previewData.statement_no}</p>
                    {previewData.processed_date && <p className="text-xs">Processed: {previewData.processed_date}</p>}
                    {previewData.agent_name && <p className="text-xs">Agent: {previewData.agent_name}</p>}
                  </div>
                )}
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Found {previewData.total_rows} rows. Map your columns below, then click Import.
                </div>
                
                {/* Scrollable mapping area */}
                <div className="max-h-[300px] overflow-y-auto border-2 border-slate-200 rounded-lg p-4 bg-slate-50 scrollbar-thin scrollbar-thumb-slate-300">
                  <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Scroll down to see all {systemFields.length} fields
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {systemFields.map(field => (
                      <div key={field.field} className="space-y-1">
                        <Label className="text-xs font-medium text-slate-600">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <Select 
                          value={columnMappings[field.field] || 'none'} 
                          onValueChange={(value) => handleMappingChange(field.field, value === 'none' ? '' : value)}
                        >
                          <SelectTrigger className="h-9 text-sm bg-white">
                            <SelectValue placeholder="Select column..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Not Mapped --</SelectItem>
                            {previewData.headers.map(header => (
                              <SelectItem key={header} value={header}>{header}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600">
                    Data Preview (first {Math.min(previewData.preview_data.length, 5)} rows)
                  </div>
                  <div className="overflow-x-auto max-h-[150px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {previewData.headers.slice(0, 8).map(header => (
                            <TableHead key={header} className="text-xs whitespace-nowrap">{header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.preview_data.slice(0, 5).map((row, idx) => (
                          <TableRow key={idx}>
                            {previewData.headers.slice(0, 8).map(header => (
                              <TableCell key={header} className="text-xs py-1 whitespace-nowrap">
                                {String(row[header] || '').substring(0, 30)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Results */}
            {importStep === 'result' && importResult && (
              <div className={`rounded-lg p-4 ${importResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start gap-3">
                  {importResult.success ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${importResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                      {importResult.success ? 'Import Successful!' : 'Import Failed'}
                    </p>
                    {importResult.success && (
                      <p className="text-sm text-emerald-700 mt-1">
                        {importResult.imported} commissions imported, {importResult.skipped || 0} skipped.
                      </p>
                    )}
                    {!importResult.success && (
                      <p className="text-sm text-red-700 mt-1">{importResult.message}</p>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2 text-sm text-amber-700 bg-amber-50 rounded p-2">
                        <p className="font-medium">Warnings:</p>
                        <ul className="list-disc list-inside mt-1">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {importResult.errors.length > 5 && <li>...and {importResult.errors.length - 5} more</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {importStep === 'mapping' && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setImportStep('upload')}
              >
                Back
              </Button>
            )}
            <Button 
              type="button" 
              variant="outline" 
              onClick={resetImportModal}
            >
              {importResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {importStep === 'upload' && !importResult?.success && (
              <Button 
                onClick={handlePreview}
                disabled={!selectedFile || importLoading}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="preview-btn"
              >
                {importLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" /> Next: Map Columns
                  </>
                )}
              </Button>
            )}
            {importStep === 'mapping' && (
              <Button 
                onClick={handleImport}
                disabled={importLoading}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="import-btn"
              >
                {importLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Import Data
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} selected commission record(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedIds.size} Record(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch PDF Import Modal */}
      <Dialog open={showBatchPdfModal} onOpenChange={(open) => !open && resetBatchPdfModal()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Batch PDF Import - DLC Commission Statements
            </DialogTitle>
            <DialogDescription>
              Upload multiple DLC Commission Statement PDFs at once. They will be automatically parsed and imported with default column mappings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File Selection */}
            {!batchImportResult && (
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                  ${batchPdfFiles.length > 0 ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}
                onClick={() => batchFileInputRef.current?.click()}
              >
                <input
                  ref={batchFileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleBatchPdfSelect}
                  className="hidden"
                  data-testid="batch-pdf-input"
                />
                {batchPdfFiles.length > 0 ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-10 h-10 text-red-500 mx-auto" />
                    <p className="font-medium text-slate-900">{batchPdfFiles.length} PDF file(s) selected</p>
                    <div className="text-sm text-slate-600 max-h-32 overflow-auto">
                      {batchPdfFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-center gap-2">
                          <FileText className="w-3 h-3 text-red-500" />
                          <span>{f.name}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setBatchPdfFiles([]); 
                        if (batchFileInputRef.current) batchFileInputRef.current.value = '';
                      }}
                      className="text-slate-500"
                    >
                      Clear selection
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-10 h-10 text-red-400" />
                    <p className="font-medium text-slate-700">Click to select multiple PDF files</p>
                    <p className="text-sm text-slate-500">DLC Commission Statement PDFs only</p>
                  </div>
                )}
              </div>
            )}

            {/* Import Results */}
            {batchImportResult && !batchImportResult.error && (
              <div className="space-y-4">
                <div className="bg-emerald-50 rounded-lg p-4 text-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Batch Import Complete!</span>
                  </div>
                  <p className="text-sm">
                    Imported {batchImportResult.total_imported} records from {batchImportResult.files_processed} files.
                    {batchImportResult.total_skipped > 0 && ` (${batchImportResult.total_skipped} rows skipped)`}
                  </p>
                </div>

                {batchImportResult.created_agents?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3 text-blue-800 text-sm">
                    <p className="font-medium mb-1">New agents created:</p>
                    <ul className="list-disc list-inside">
                      {batchImportResult.created_agents.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}

                <div className="max-h-64 overflow-auto space-y-2">
                  {batchImportResult.results?.map((r, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${r.status === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{r.filename}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${r.status === 'success' ? 'bg-emerald-200 text-emerald-800' : 'bg-red-200 text-red-800'}`}>
                          {r.status}
                        </span>
                      </div>
                      {r.status === 'success' && (
                        <p className="text-xs text-slate-600 mt-1">
                          Statement #{r.statement_no} | {r.processed_date} | Imported: {r.imported}
                        </p>
                      )}
                      {r.status !== 'success' && r.reason && (
                        <p className="text-xs text-red-600 mt-1">{r.reason}</p>
                      )}
                    </div>
                  ))}
                </div>

                {batchImportResult.errors?.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-3 text-amber-800 text-sm">
                    <p className="font-medium mb-1">Warnings:</p>
                    <ul className="list-disc list-inside text-xs max-h-24 overflow-auto">
                      {batchImportResult.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {batchImportResult?.error && (
              <div className="bg-red-50 rounded-lg p-4 text-red-800">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Import Failed</span>
                </div>
                <p className="text-sm mt-1">{batchImportResult.error}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetBatchPdfModal}>
              {batchImportResult ? 'Close' : 'Cancel'}
            </Button>
            {!batchImportResult && (
              <Button
                onClick={handleBatchPdfImport}
                disabled={batchPdfFiles.length === 0 || batchImportLoading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="batch-import-btn"
              >
                {batchImportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Import {batchPdfFiles.length} PDF(s)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
